/**
 * The charts on the Sales overview.
 *
 * Three rules run through all of them, and they are the reason these live in
 * one file rather than inline on the page:
 *
 *   **One hue, not a rainbow.** Every series here is a magnitude — how many
 *   leads, how many at each stage — and magnitude is a sequential encoding:
 *   one hue, light to dark. A categorical palette would claim the stages are
 *   unrelated identities, which is exactly what they are not.
 *
 *   **Every mark is labelled.** The ramp's lighter steps do not clear 3:1
 *   against the page, so the number never depends on reading the colour. It is
 *   printed beside the mark, and the tooltip repeats it.
 *
 *   **Both themes are chosen, not flipped.** The ramp is redefined for dark
 *   mode against the dark surface rather than left to invert itself.
 */

/** Ramp steps, light → dark. CSS owns the values so both themes are explicit. */
const RAMP = ['var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c5)'];

/** Pick a ramp step for position `i` of `n`, darkest last. */
const step = (i, n) => RAMP[Math.min(RAMP.length - 1, Math.round((i / Math.max(1, n - 1)) * (RAMP.length - 1)))];

/* ------------------------------------------------------------ rank bars */

/**
 * A ranked magnitude, biggest first — the workhorse of this page.
 *
 * Horizontal because the labels are words: a source or a stage name set
 * sideways under a vertical bar is unreadable at any width worth having.
 */
export function RankBars({ rows, total, empty = 'Nothing yet', max: cap }) {
  if (!rows.length) return <div className="cempty">{empty}</div>;

  const max = Math.max(1, ...rows.map(r => r.value));
  const shown = cap ? rows.slice(0, cap) : rows;

  return (
    <div className="rbars">
      {shown.map((r, i) => (
        <div className="rbar" key={r.label}>
          <div className="rlab">
            <span title={r.label}>{r.label}</span>
            <b>
              {r.value}
              {total ? <em>{Math.round((r.value / total) * 100)}%</em> : null}
            </b>
          </div>
          <div className="rtrack">
            <div
              className="rfill"
              style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: step(i, shown.length) }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- funnel */

/**
 * Each step as a share of the one above it.
 *
 * The drop between two steps is the thing worth reading, so it is written out
 * rather than left to be estimated from two bar lengths — the whole point of a
 * funnel is the ratio, and a ratio is a number.
 */
export function Funnel({ steps }) {
  const top = Math.max(1, steps[0]?.value || 0);

  return (
    <div className="funnel">
      {steps.map((s, i) => {
        const prev = i ? steps[i - 1].value : null;
        const drop = prev ? Math.round((s.value / Math.max(1, prev)) * 100) : null;

        return (
          <div className="fstep" key={s.label}>
            <div className="flab">
              <span>{s.label}</span>
              <b>{s.value}</b>
            </div>
            <div className="ftrack">
              <div
                className="ffill"
                style={{ width: `${Math.max(2, (s.value / top) * 100)}%`, background: step(i, steps.length) }}
              />
            </div>
            <div className="fhint">
              {s.hint}
              {drop !== null ? <span className="fdrop">{drop}% of the step above</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
